import type { MascotState } from './mascot.types';

export const POSE_BY_ANIMATION = {
  idle: 'idle', wink: 'wink', 'happy-jump': 'happy-jump', 'show-love': 'love',
  curious: 'curious', pounce: 'pounce', 'thank-you': 'thank-you', sleep: 'sleep',
  peek: 'peek', follow: 'follow', starting: 'idle', gathering: 'gathering',
  working: 'working', 'almost-there': 'almost-there', done: 'happy-jump',
} as const satisfies Record<MascotState, string>;

export type MascotPose = typeof POSE_BY_ANIMATION[MascotState];
export const POSE_IDS = [...new Set(Object.values(POSE_BY_ANIMATION))];

/**
 * Public mascot URLs carry a content fingerprint because the CDN deliberately serves public
 * assets as immutable for a year. Keeping the mapping explicit also makes preloading a pose
 * possible before the rigged mascot/GSAP chunk has loaded.
 */
const POSE_SOURCES: Record<MascotPose, string> = {
  'almost-there': '/mascot/poses/almost-there-be86cfcf.webp',
  curious: '/mascot/poses/curious-6abad99d.webp',
  follow: '/mascot/poses/follow-40bb708e.webp',
  gathering: '/mascot/poses/gathering-c974e53d.webp',
  'happy-jump': '/mascot/poses/happy-jump-056224ed.webp',
  idle: '/mascot/poses/idle-42bc23b2.webp',
  love: '/mascot/poses/love-29a0e9da.webp',
  peek: '/mascot/poses/peek-03c45d7b.webp',
  pounce: '/mascot/poses/pounce-9e9bab92.webp',
  sleep: '/mascot/poses/sleep-e4291ec4.webp',
  'thank-you': '/mascot/poses/thank-you-2978e9bd.webp',
  wink: '/mascot/poses/wink-938e31dd.webp',
  working: '/mascot/poses/working-18aa60cc.webp',
};

export const poseSource = (pose: MascotPose) => POSE_SOURCES[pose];

/** A failed non-idle pose can fall back to idle; idle itself must never retry itself. */
export const fallbackPose = (failedPose: MascotPose): MascotPose | null =>
  failedPose === 'idle' ? null : 'idle';

const preloaded = new Set<MascotPose>();

/** Starts a browser fetch/decode without mounting an extra visible image. */
export function preloadPose(pose: MascotPose) {
  if (typeof Image === 'undefined' || preloaded.has(pose)) return;
  preloaded.add(pose);
  const image = new Image();
  image.decoding = 'async';
  image.src = poseSource(pose);
  void image.decode?.().catch(() => {
    // MascotArtwork owns the visible error fallback. A speculative preload must stay best-effort.
  });
}

export const MASCOT_SIZES = { xs: 80, sm: 120, md: 240, lg: 320, hero: 400 } as const;
export type MascotSize = keyof typeof MASCOT_SIZES;

export const POSE_NOTES: Record<MascotPose, string> = {
  idle: 'Front-facing seated pose, relaxed paws, one bent ear.',
  wink: 'Seated greeting, tilted head and one closed eye.',
  'happy-jump': 'Airborne, raised arms, separated feet, paw pads and open joyful mouth.',
  love: 'Both paws wrap around a large heart at the chest.',
  curious: 'Asymmetric seat, tilted head and outward floppy ear.',
  pounce: 'Four paws, horizontal torso, front paws extended and rear raised.',
  'thank-you': 'Paws together below the chin, closed eyes and gentle bow.',
  sleep: 'Lying horizontally with head resting on forepaws and relaxed ears.',
  peek: 'Upright leaning silhouette with paws holding a vertical edge.',
  follow: 'Seated, with eyes and head attending to the butterfly.',
  gathering: 'Turns toward and brings in the hourglass.',
  working: 'Leans forward with paws reaching to interact with the hourglass.',
  'almost-there': 'Waits behind the hourglass with paws resting on the upper rim.',
};
